import { useState } from 'react';

import { Button } from '@llura/ui';
import { FlowSteps } from '@llura/constants';

import { useStrings } from '../../../hooks/useStrings';
import { useStepTiming } from '../../../lib/analytics';
import {
  useRitualStore,
  type RitualIntensity,
  type RitualPeriod,
} from '../../../stores/ritual.store';
import { CadencePicker, type CadenceValue } from '../../ritual/cadence-picker';
import { IntroScreen } from '../intro-screen';
import { IntroMark } from '../intro-mark';

/** Outcome of the ritual screen — `set` if confirmed, `skip` if deferred. */
export type RitualOutcome =
  | { kind: 'set'; count: number; period: RitualPeriod; intensity: RitualIntensity | null }
  | { kind: 'skip' };

interface IntroRitualProps {
  onBack: () => void;
  onComplete: (outcome: RitualOutcome) => void;
  onLogin: () => void;
}

export function IntroRitual({ onBack, onComplete, onLogin }: IntroRitualProps) {
  useStepTiming(FlowSteps.IntroScreen4);
  const { strings } = useStrings();
  const setCadence = useRitualStore((s) => s.setCadence);

  // Seed from persisted values so back-navigation feels continuous. The picker
  // is confirm-gated here (unlike the settings screen), so we hold a local
  // draft and only persist on confirm.
  const persistedCount = useRitualStore((s) => s.count);
  const persistedPeriod = useRitualStore((s) => s.period);
  const persistedIntensity = useRitualStore((s) => s.intensity);

  const [cadence, setCadenceDraft] = useState<CadenceValue>({
    count: persistedCount,
    period: persistedPeriod,
    intensity: persistedIntensity,
  });

  const confirm = () => {
    if (cadence.count == null || cadence.period == null) return;
    setCadence({
      count: cadence.count,
      period: cadence.period,
      intensity: cadence.intensity,
    });
    onComplete({
      kind: 'set',
      count: cadence.count,
      period: cadence.period,
      intensity: cadence.intensity,
    });
  };

  const skip = () => {
    onComplete({ kind: 'skip' });
  };

  const canConfirm = cadence.count != null && cadence.period != null;

  return (
    <IntroScreen
      step={4}
      tag={strings.intro.screen5.tag}
      title={strings.intro.screen5.title}
      italic={strings.intro.screen5.italic}
      body={strings.intro.screen5.body}
      visual={<IntroMark variant="pulse" />}
      onBack={onBack}
      onLogin={onLogin}
      onNext={canConfirm ? confirm : undefined}
      nextLabel={strings.intro.screen5.confirmCta}
      extra={<CadencePicker value={cadence} onChange={setCadenceDraft} />}
      secondaryCta={
        <Button
          variant="ghost"
          onClick={skip}
          className="w-full text-xs text-muted-foreground hover:text-foreground"
        >
          {strings.intro.screen5.skipCta}
        </Button>
      }
    />
  );
}
